import React, { useState } from "react"
import {
  Box, FormControl, FormLabel, HStack, Input, Popover, PopoverBody, PopoverContent, PopoverTrigger,
  SimpleGrid, Stack, useDisclosure,
} from "@chakra-ui/react"
import Button from "components/Buttons/Button"
import {
  ActivityRange, lastDays, rangeLabel,
} from "../activitySeries"

type Props = {
  range: ActivityRange
  // The dates as picked, when the chart shows them widened to whole weeks or months.
  picked?: ActivityRange
  onApply: (range: ActivityRange) => void
  today: string
}

const DateRangeButton = ({
  range, picked = range, onApply, today,
}: Props) => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [from, setFrom] = useState(picked.from ?? "")
  const [to, setTo] = useState(picked.to)
  const open = () => {
    setFrom(picked.from ?? "")
    setTo(picked.to)
    onOpen()
  }
  const presets = [
    { label: "Last 7 days", range: lastDays(7, today) },
    { label: "Last 30 days", range: lastDays(30, today) },
    { label: "Last 90 days", range: lastDays(90, today) },
    { label: "All time", range: { to: today } },
  ]
  const apply = (value: ActivityRange) => { onApply(value); onClose() }
  const valid = Boolean(from && to && from <= to)

  return (
    <Popover isOpen={isOpen} onOpen={open} onClose={onClose} placement="bottom-end">
      <PopoverTrigger>
        <Box display="inline-flex" flexShrink={0}>
          <Button
            size="sm"
            variant="outline"
            colorScheme="gray"
            leftIcon="event-date"
            borderRadius="8px"
            fontSize="xs"
          >
            {rangeLabel(range)}
          </Button>
        </Box>
      </PopoverTrigger>
      <PopoverContent w="2xs">
        <PopoverBody p={4}>
          <Stack spacing={3}>
            <SimpleGrid columns={2} spacing={2}>
              {presets.map((preset) => (
                <Button
                  key={preset.label}
                  size="sm"
                  variant="outline"
                  colorScheme="gray"
                  onClick={() => apply(preset.range)}
                >
                  {preset.label}
                </Button>
              ))}
            </SimpleGrid>
            <FormControl>
              <FormLabel fontSize="xs">From</FormLabel>
              <Input type="date" size="sm" value={from} max={to} onChange={(e) => setFrom(e.target.value)} />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs">To</FormLabel>
              <Input type="date" size="sm" value={to} min={from} onChange={(e) => setTo(e.target.value)} />
            </FormControl>
            <HStack justify="flex-end">
              <Button size="sm" variant="outline" colorScheme="gray" onClick={onClose}>Cancel</Button>
              <Button
                size="sm"
                isDisabled={!valid}
                onClick={() => apply({ from, to })}
              >
                Apply
              </Button>
            </HStack>
          </Stack>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  )
}

export default DateRangeButton
